import { Fragment } from 'react';
import { Badge, Inline, Stack } from '@etg/wings';
import { css, mediaQueries } from '@eti/styles';
import { TripType } from '@eti/schema-types';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import {
  boundIncludesTrainSegments,
  getBoundAsSegment,
} from '../../../../../../widgets/trip-details/utils/format';
import { getUniqueMarketingCarriersFromSegments } from '../../../../../../utils/carriers';
import type { BoundsType, TripTypeType } from '../../../../models';
import type { RebookingTripBoundsType } from '../../../../../change-trip/models';
import type { SearchRouteCodeKindType } from '../../model';
import { SearchRouteCodeKind } from '../../model';
import {
  isMatchingLocation,
  isTripWithDifferentOriginOrDestination,
} from '../../../../../../widgets/trip-details/utils/originDestinationUtil';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { TripDetailsBoundSmall } from './tripDetailsBound/TripDetailsBoundSmall';
import { TripDetailsBoundA } from './tripDetailsBound/TripDetailsBoundA';
import { TripDetailsBoundB } from './tripDetailsBound/TripDetailsBoundB';

const tripDetailsBoundWrapper = css`
  flex: 1;
  justify-content: center;

  @media ${mediaQueries.small.only} {
    padding-bottom: 8px;
  }
`;

const badgeContainerStyles = css`
  padding: 8px 16px;
`;

interface TripDetailsBoundsProps {
  boundsWithLegends: BoundsType | RebookingTripBoundsType;
  flightId: string;
  shouldShowOneWayCombinationInfo?: boolean;
  type: TripTypeType;
}

const TripDetailsBounds = ({
  boundsWithLegends,
  flightId,
  shouldShowOneWayCombinationInfo,
  type,
}: TripDetailsBoundsProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();

  const isMobileDevice = window.matchMedia(mediaQueries.small.only).matches;
  const isMultiStop = type === TripType.MultiStop;
  const tripCardVersion = p('Result.Page.TripCard.Version');
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const { currentSearchData } = useSearch();

  const tripHasDifferentOriginOrDestination = isTripWithDifferentOriginOrDestination(
    currentSearchData?.routes ?? [],
    boundsWithLegends,
  );
  const shouldShowDifferentAirportBadge =
    tripHasDifferentOriginOrDestination && isDifferentAirportNotificationEnabled;

  const TripDetailsBound = tripCardVersion === 'v3a' ? TripDetailsBoundA : TripDetailsBoundB;

  const isDifferentAirport = (
    boundNumber: number,
    code: string,
    cityCode: string,
    searchRouteKind: SearchRouteCodeKindType,
  ) => {
    return (
      !isChangeTripPage &&
      isDifferentAirportNotificationEnabled &&
      !isMatchingLocation(currentSearchData?.routes?.[boundNumber][searchRouteKind] ?? '', {
        code,
        cityCode,
      })
    );
  };

  const getBadgesSRText = () => {
    const textsArray: string[] = [];

    if (!isChangeTripPage && shouldShowDifferentAirportBadge) {
      textsArray.push(t('Result.NearbyAirports.Badge.Text'));
    }

    if (shouldShowOneWayCombinationInfo) {
      textsArray.push(t('Air.TripInformation.OneWayCombination'));
    }

    return textsArray.join();
  };

  return (
    <Stack className={tripDetailsBoundWrapper} spacing={0}>
      {boundsWithLegends.map(
        ({ includedCabinBaggage, includedCheckedBaggage, segments }: any, index: number) => (
          <Fragment key={`${flightId! + index}`}>
            {isMobileDevice && (
              <TripDetailsBoundSmall
                boundAsSegment={getBoundAsSegment(
                  segments,
                  index + 1,
                  includedCheckedBaggage,
                  includedCabinBaggage,
                )}
                boundNumber={index + 1}
                flightId={flightId!}
                includesTrainSegments={boundIncludesTrainSegments(segments)}
                isDifferentDestinationAirport={isDifferentAirport(
                  index,
                  segments[segments.length - 1].destination.code,
                  segments[segments.length - 1].destination.cityCode,
                  SearchRouteCodeKind.DESTINATION_CODE,
                )}
                isDifferentOriginAirport={isDifferentAirport(
                  index,
                  segments[0].origin.code,
                  segments[0].origin.cityCode,
                  SearchRouteCodeKind.ORIGIN_CODE,
                )}
                marketingCarriers={getUniqueMarketingCarriersFromSegments(segments)}
                segments={segments}
              />
            )}
            {!isMobileDevice && (
              <TripDetailsBound
                boundAsSegment={getBoundAsSegment(
                  segments,
                  index + 1,
                  includedCheckedBaggage,
                  includedCabinBaggage,
                )}
                boundNumber={index + 1}
                flightId={flightId!}
                includesTrainSegments={boundIncludesTrainSegments(segments)}
                isDifferentDestinationAirport={isDifferentAirport(
                  index,
                  segments[segments.length - 1].destination.code,
                  segments[segments.length - 1].destination.cityCode,
                  SearchRouteCodeKind.DESTINATION_CODE,
                )}
                isDifferentOriginAirport={isDifferentAirport(
                  index,
                  segments[0].origin.code,
                  segments[0].origin.cityCode,
                  SearchRouteCodeKind.ORIGIN_CODE,
                )}
                isMultiStop={isMultiStop}
                marketingCarriers={getUniqueMarketingCarriersFromSegments(segments)}
                segments={segments}
              />
            )}
          </Fragment>
        ),
      )}

      {(shouldShowDifferentAirportBadge || shouldShowOneWayCombinationInfo) && (
        <Inline aria-label={getBadgesSRText()} className={badgeContainerStyles}>
          {!isChangeTripPage && shouldShowDifferentAirportBadge && (
            <Badge aria-hidden data-testid="nearby-airports-badge" variant="caution">
              {t('Result.NearbyAirports.Badge.Text')}
            </Badge>
          )}
          {shouldShowOneWayCombinationInfo && (
            <Badge aria-hidden data-testid="one-way-combination-badge" variant="information">
              {t('Air.TripInformation.OneWayCombination')}
            </Badge>
          )}
        </Inline>
      )}
    </Stack>
  );
};

export default TripDetailsBounds;
